import { formatKeys, isApplePlatform, normalizeKeys, parseKeys, type ParsedKeys } from "./keys";
import type { Binding, Scope } from "./types";

/**
 * The single source of truth for every keyboard shortcut in the app.
 *
 * Both delivery paths — the window dispatcher and the editor's ProseMirror
 * bridge — read from here, so a shortcut is declared exactly once no matter
 * which path ends up carrying it.
 *
 * Registration is a plain module-level side effect: `bindings/index.ts` calls
 * `register()` at import time, and `ShortcutProvider` imports that module.
 */

interface RegisteredBinding extends Binding {
  parsed: ParsedKeys;
  /** Resolved combination — `keys` today, a user override in future. */
  resolvedKeys: string;
}

const byId = new Map<string, RegisteredBinding>();
/** `${scope}::${normalizedKeys}` → binding id, for collision detection. */
const bySlot = new Map<string, string>();

/**
 * Where user-defined remappings would land. Deliberately empty and unwritten:
 * every read of a binding's keys already goes through `resolveKeys`, so adding
 * remapping later is a matter of populating this map and re-indexing, not of
 * touching each binding site.
 */
const overrides = new Map<string, string>();

/**
 * The one place a binding's effective combination is decided. Platform choice
 * happens here, and a user override would slot in here too — which is why no
 * other code reads `binding.keys` directly.
 */
function resolveKeys(binding: Binding): string {
  const override = overrides.get(binding.id);
  if (override) return override;

  if (typeof binding.keys === "string") return binding.keys;
  return isApplePlatform() ? binding.keys.apple : binding.keys.other;
}

function slotOf(scope: Scope, keys: string): string {
  return `${scope}::${normalizeKeys(keys)}`;
}

export function register(binding: Binding): void {
  const resolvedKeys = resolveKeys(binding);
  const slot = slotOf(binding.scope, resolvedKeys);

  if (byId.has(binding.id)) {
    throw new Error(`Duplicate shortcut id "${binding.id}"`);
  }

  const existing = bySlot.get(slot);
  if (existing) {
    // The whole point of the registry. Two extensions silently claiming the
    // same combination used to mean one of them just never fired.
    throw new Error(
      `Shortcut collision: "${resolvedKeys}" is already bound to "${existing}" in scope "${binding.scope}". ` +
        `Cannot also bind "${binding.id}".`,
    );
  }

  byId.set(binding.id, {
    ...binding,
    resolvedKeys,
    parsed: parseKeys(resolvedKeys),
  });
  bySlot.set(slot, binding.id);
}

export function registerAll(bindings: Binding[]): void {
  for (const binding of bindings) register(binding);
}

export function unregister(id: string): void {
  const binding = byId.get(id);
  if (!binding) return;
  bySlot.delete(slotOf(binding.scope, binding.resolvedKeys));
  byId.delete(id);
}

/** Dispatchable bindings for a scope, in registration order. */
export function getBindings(scope: Scope): RegisteredBinding[] {
  const out: RegisteredBinding[] = [];
  for (const binding of byId.values()) {
    if (binding.scope === scope && !binding.documentationOnly) out.push(binding);
  }
  return out;
}

/** Bindings the editor's ProseMirror keymap is responsible for. */
export function getProseMirrorBindings(): RegisteredBinding[] {
  const out: RegisteredBinding[] = [];
  for (const binding of byId.values()) {
    if (binding.viaProseMirror && !binding.documentationOnly) out.push(binding);
  }
  return out;
}

/** Every binding including documentation-only ones, for the help UI. */
export function getAllBindings(): RegisteredBinding[] {
  return Array.from(byId.values());
}

export function getBinding(id: string): RegisteredBinding | undefined {
  return byId.get(id);
}

/**
 * Display string for a shortcut, or null when nothing is bound.
 *
 * Menus render their hints through this, which means a menu can only ever show
 * a shortcut that genuinely exists — a hint and its behavior cannot drift apart.
 */
export function shortcutLabel(id: string): string | null {
  const binding = byId.get(id);
  return binding ? formatKeys(binding.resolvedKeys) : null;
}

/** Test seam. Not used in app code. */
export function resetRegistry(): void {
  byId.clear();
  bySlot.clear();
  overrides.clear();
}
